import { useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { BottomNav } from '@/components/client/BottomNav';
import { lireInvitation, oublierInvitation } from '@/lib/invitation';
import { useVoteWindowStore } from '@/stores/voteWindowStore';

const HIDE_NAV_ROUTES = [
  '/order',
  '/order/success',
  '/credits/formula',
  '/credits/manual',
  '/credits/auto',
  '/distribution',
];

// Correspondance par segment, pas par préfixe brut : `/order` (le flux de
// commande, nav masquée) ne doit pas avaler `/orders` (la liste des
// commandes, nav visible — c'est un écran de consultation, pas un tunnel).
function estRouteSansNav(pathname: string): boolean {
  return HIDE_NAV_ROUTES.some((r) => pathname === r || pathname.startsWith(`${r}/`));
}

/**
 * Mobile-first client shell: full-bleed content with BottomNav floating
 * over it. Navigation is hidden on focused flow screens (order, payment).
 */
export function ClientLayout() {
  const { pathname } = useLocation();
  const showNav = !estRouteSansNav(pathname);

  // Code d'invitation conservé pendant la connexion/inscription (ADR 0008) :
  // au premier montage du shell, on ramène le client sur /a/:code. On oublie
  // le code IMMÉDIATEMENT (avant même de naviguer, revue T13) : un rebond
  // unique, jamais une boucle si /a/:code retombe sur un état terminal.
  const navigate = useNavigate();
  useEffect(() => {
    const code = lireInvitation();
    if (code) {
      oublierInvitation();
      navigate(`/a/${code}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Amorce l'état de fenêtre de vote pour le badge nav (design-handoff item
  // 8) au montage du shell, puis rafraîchit toutes les 5 min tant que le
  // shell reste monté (session laissée ouverte sans navigation, cf. revue
  // chantier 7 tâche 4) — `refresh` est une référence stable (méthode du
  // store zustand), pas de cascade d'effets (CLAUDE.md) : le throttle
  // interne du store (5 min) absorbe les appels rapprochés, cet intervalle
  // se contente de garantir qu'un appel arrive au moins toutes les 5 min.
  const refreshVoteWindow = useVoteWindowStore((s) => s.refresh);
  useEffect(() => {
    void refreshVoteWindow();
    const REFRESH_INTERVAL_MS = 5 * 60_000;
    const id = setInterval(() => {
      void refreshVoteWindow();
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [refreshVoteWindow]);

  return (
    <div className="mx-auto min-h-[100dvh] max-w-phone overflow-x-hidden bg-orange-50 text-ink dark:bg-d-bg dark:text-d-ink">
      <main className={showNav ? 'pb-28' : 'pb-safe'}>
        <Outlet />
      </main>
      {showNav && <BottomNav />}
    </div>
  );
}
